import { useMemo, useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, ClipboardList, CalendarCheck, StickyNote,
  Plus, Trash2, ChevronRight, AlertCircle, Clock,
} from 'lucide-react';
import clsx from 'clsx';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { displayStatus } from '@/lib/types';
import type { Demanda, Evento, Anotacao } from '@/lib/types';
import { DistribuicoesPendentesWidget } from '@/components/DistribuicoesPendentesWidget';
import { InfosEventosWidget } from '@/components/InfosEventosWidget';

/* ── Notes migration helpers ── */
const NOTES_KEY = 'apex_notes_v2';
const MIGRATED_KEY = 'apex_notes_migrated';
interface LegacyNote { id: string; title: string; body: string; updatedAt: number }
function loadLegacyNotes(): LegacyNote[] {
  try { return JSON.parse(localStorage.getItem(NOTES_KEY) ?? '[]'); } catch { return []; }
}

/** Encode title + body into a single texto field (first line = title) */
function encodeNota(title: string, body: string): string {
  return title ? `${title}\n${body}` : body;
}
/** Decode texto back into title + body */
function decodeNota(texto: string): { title: string; body: string } {
  const idx = texto.indexOf('\n');
  if (idx === -1) return { title: texto, body: '' };
  return { title: texto.slice(0, idx), body: texto.slice(idx + 1) };
}

/* ── Helpers ── */
const today = () => new Date().toISOString().slice(0, 10);

const STATUS_COR: Record<string, string> = {
  'Triagem':               'bg-yellow/15 text-yellow border-yellow/30',
  'Em produção':           'bg-blue/15 text-blue-light border-blue/30',
  'Em revisão':            'bg-purple/15 text-purple border-purple/30',
  'Aguardando aprovação':  'bg-orange/15 text-orange border-orange/30',
  'Publicada':             'bg-green/15 text-green border-green/30',
};

const PRIORIDADE_COR: Record<string, string> = {
  'Crítica': 'text-red',
  'Alta':    'text-orange',
  'Média':   'text-yellow',
  'Baixa':   'text-muted',
};

export function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();

  /* ── Queries ── */
  const demandasQ = useQuery({
    queryKey: ['demandas'],
    queryFn: () => api.demandas.list().then((r) => r.data),
    staleTime: 30_000,
  });
  const eventosQ = useQuery({
    queryKey: ['eventos'],
    queryFn: () => api.eventos.list().then((r) => r.data),
    staleTime: 60_000,
  });

  const demandas: Demanda[] = demandasQ.data ?? [];
  const eventos: Evento[] = eventosQ.data ?? [];

  /* ── Minhas demandas ativas ── */
  const minhasDemandas = useMemo(() => {
    if (!user?.pessoaId) return [];
    const active = ['Triagem', 'Em produção', 'Em revisão', 'Aguardando aprovação'];
    return demandas
      .filter((d) =>
        active.includes(d.status) &&
        (d.executorId === user.pessoaId ||
         d.coordenadorId === user.pessoaId ||
         d.respBriefingIds?.includes(user.pessoaId!) ||
         d.respProducaoIds?.includes(user.pessoaId!) ||
         d.respRevisaoIds?.includes(user.pessoaId!))
      )
      .sort((a, b) => {
        const pri = { 'Crítica': 0, 'Alta': 1, 'Média': 2, 'Baixa': 3 } as Record<string, number>;
        return (pri[a.prioridade] ?? 9) - (pri[b.prioridade] ?? 9);
      })
      .slice(0, 12);
  }, [demandas, user]);

  /* ── Entregas do dia (publicadas hoje) ── */
  const entregasHoje = useMemo(() => {
    const t = today();
    return demandas
      .filter((d) => d.status === 'Publicada' && d.dataPublicacao?.startsWith(t))
      .slice(0, 8);
  }, [demandas]);

  /* ── Próximos eventos ── */
  const proximosEventos = useMemo(() => {
    const t = today();
    return eventos
      .filter((e) => e.dataInicio && e.dataInicio >= t && e.status !== 'Cancelado')
      .sort((a, b) => (a.dataInicio ?? '').localeCompare(b.dataInicio ?? ''))
      .slice(0, 6);
  }, [eventos]);

  /* ── Notes (API-backed) ── */
  const qc = useQueryClient();
  const userEmail = user?.email ?? 'home';
  const notasQ = useQuery({
    queryKey: ['anotacoes', 'geral', userEmail],
    queryFn: () => api.anotacoes.list('geral', userEmail).then((r) => r.data),
    staleTime: 30_000,
  });
  const notas: Anotacao[] = notasQ.data ?? [];
  const [editingId, setEditingId] = useState<string | null>(null);

  // Migration: push localStorage notes to API once
  useEffect(() => {
    if (!user?.email) return;
    if (localStorage.getItem(MIGRATED_KEY)) return;
    const legacy = loadLegacyNotes();
    if (legacy.length === 0) {
      localStorage.setItem(MIGRATED_KEY, '1');
      return;
    }
    (async () => {
      for (const n of legacy) {
        try {
          await api.anotacoes.create({ texto: encodeNota(n.title, n.body), recursoTipo: 'geral', recursoId: user.email });
        } catch { /* best-effort */ }
      }
      localStorage.setItem(MIGRATED_KEY, '1');
      localStorage.removeItem(NOTES_KEY);
      qc.invalidateQueries({ queryKey: ['anotacoes', 'geral', user.email] });
    })();
  }, [user?.email, qc]);

  const criarMut = useMutation({
    mutationFn: (texto: string) => api.anotacoes.create({ texto, recursoTipo: 'geral', recursoId: userEmail }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['anotacoes', 'geral', userEmail] });
      setEditingId(r.data.id);
    },
  });
  const updateMut = useMutation({
    mutationFn: ({ id, texto }: { id: string; texto: string }) => api.anotacoes.update(id, texto),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['anotacoes', 'geral', userEmail] }),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => api.anotacoes.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['anotacoes', 'geral', userEmail] }),
  });

  const criarNota = useCallback(() => {
    criarMut.mutate(encodeNota('Nova nota', ''));
  }, [criarMut]);

  const updateNota = useCallback((id: string, patch: { title?: string; body?: string }) => {
    const nota = notas.find((n) => n.id === id);
    if (!nota) return;
    const cur = decodeNota(nota.texto);
    const next = encodeNota(patch.title ?? cur.title, patch.body ?? cur.body);
    updateMut.mutate({ id, texto: next });
  }, [notas, updateMut]);

  const removerNota = useCallback((id: string) => {
    deleteMut.mutate(id);
    if (editingId === id) setEditingId(null);
  }, [deleteMut, editingId]);

  const editingNote = editingId ? notas.find((n) => n.id === editingId) : null;
  const editingDecoded = editingNote ? decodeNota(editingNote.texto) : null;

  const firstName = user?.nome?.split(' ')[0] ?? 'Olá';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-lg font-bold">Bom dia, {firstName}</h1>
        <p className="text-xs text-muted mt-0.5">
          {new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
        </p>
      </div>

      {/* S1: widget por papel — Comercial vê distribuições pendentes */}
      {(user?.papel === 'Comercial' || user?.papel === 'Coordenação') && (
        <DistribuicoesPendentesWidget demandas={demandas} />
      )}

      {/* S2 §2.3 — INFOs de evento (informar/aplicar) espelhadas na Home */}
      <InfosEventosWidget demandas={demandas} eventos={eventos} />


      {/* Grid: 2 colunas em desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* ── MINHAS DEMANDAS ── */}
        <Card
          icon={ClipboardList}
          title="Minhas demandas"
          count={minhasDemandas.length}
          action={{ label: 'Ver todas', onClick: () => navigate('/minhas-demandas') }}
        >
          {minhasDemandas.length === 0 ? (
            <Empty text="Nenhuma demanda ativa atribuída a você." />
          ) : (
            <div className="space-y-1">
              {minhasDemandas.map((d) => (
                <div key={d.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-panel-2 transition-colors">
                  <span className={clsx('w-1.5 h-1.5 rounded-full shrink-0', PRIORIDADE_COR[d.prioridade]?.replace('text-', 'bg-') || 'bg-muted')} />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium truncate">{d.titulo}</div>
                    <div className="text-[10px] text-muted truncate">{d.solicitanteNome}</div>
                  </div>
                  <span className={clsx(
                    'text-[10px] px-1.5 py-0.5 rounded-full border font-medium shrink-0',
                    STATUS_COR[d.status] ?? 'bg-panel-2 text-muted border-border',
                  )}>
                    {displayStatus(d.status)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* ── ENTREGAS DO DIA ── */}
        <Card
          icon={CalendarCheck}
          title="Entregas do dia"
          count={entregasHoje.length}
          action={{ label: 'Entregas', onClick: () => navigate('/entregas') }}
        >
          {entregasHoje.length === 0 ? (
            <Empty text="Nenhuma entrega publicada hoje." />
          ) : (
            <div className="space-y-1">
              {entregasHoje.map((d) => (
                <div key={d.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-panel-2 transition-colors">
                  <CalendarCheck className="w-3 h-3 text-green shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium truncate">{d.titulo}</div>
                    <div className="text-[10px] text-muted truncate">{d.solicitanteNome}</div>
                  </div>
                  {d.linkFinal && (
                    <a href={d.linkFinal} target="_blank" rel="noreferrer" className="text-[10px] text-blue-light hover:text-blue">
                      link
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* ── PRÓXIMOS EVENTOS ── */}
        <Card
          icon={Clock}
          title="Próximos eventos"
          count={proximosEventos.length}
          action={{ label: 'Eventos', onClick: () => navigate('/eventos') }}
        >
          {proximosEventos.length === 0 ? (
            <Empty text="Nenhum evento próximo." />
          ) : (
            <div className="space-y-1">
              {proximosEventos.map((ev) => (
                <div key={ev.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-panel-2 transition-colors">
                  <div className="text-center shrink-0 w-9">
                    <div className="text-[9px] uppercase text-muted leading-tight">
                      {ev.dataInicio ? new Date(ev.dataInicio + 'T12:00:00').toLocaleDateString('pt-BR', { month: 'short' }) : '—'}
                    </div>
                    <div className="text-sm font-bold text-text leading-tight">
                      {ev.dataInicio ? new Date(ev.dataInicio + 'T12:00:00').getDate() : '—'}
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium truncate">{ev.nome}</div>
                    <div className="text-[10px] text-muted truncate">
                      {ev.local ?? ev.familia ?? ''}
                    </div>
                  </div>
                  <span className={clsx(
                    'text-[10px] px-1.5 py-0.5 rounded-full border font-medium shrink-0',
                    ev.status === 'Confirmado' ? 'bg-green/15 text-green border-green/30'
                      : ev.status === 'Realizado' ? 'bg-blue/15 text-blue-light border-blue/30'
                      : 'bg-panel-2 text-muted border-border',
                  )}>
                    {ev.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* ── NOTES ── */}
        <Card
          icon={StickyNote}
          title="Notes"
          count={notas.length}
          action={{ label: '+ nova', onClick: criarNota }}
        >
          <div className="grid grid-cols-[160px_1fr] min-h-[180px] -mx-3 -mb-1 border-t border-border mt-1">
            {/* Lista */}
            <div className="border-r border-border overflow-y-auto max-h-56 bg-panel-2/30">
              {notas.length === 0 && (
                <div className="p-3 text-[10px] text-muted text-center">
                  {notasQ.isLoading ? 'Carregando…' : 'Nenhuma nota.'}
                </div>
              )}
              {notas.map((n) => {
                const active = n.id === editingId;
                const decoded = decodeNota(n.texto);
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => setEditingId(n.id)}
                    className={clsx(
                      'w-full text-left px-2.5 py-2 border-b border-border last:border-b-0 transition-colors',
                      active ? 'bg-blue/10 text-text' : 'hover:bg-panel-2 text-text-2',
                    )}
                  >
                    <div className="text-[11px] font-semibold truncate">{decoded.title || '(sem título)'}</div>
                    <div className="text-[9px] text-muted truncate mt-0.5">{decoded.body || 'vazio…'}</div>
                  </button>
                );
              })}
            </div>
            {/* Editor */}
            <div className="flex flex-col min-w-0">
              {editingNote && editingDecoded ? (
                <>
                  <div className="px-3 py-1.5 border-b border-border flex items-center gap-1.5">
                    <input
                      className="input flex-1 text-xs font-semibold !py-1"
                      value={editingDecoded.title}
                      onChange={(e) => updateNota(editingNote.id, { title: e.target.value })}
                      placeholder="Título"
                    />
                    <button type="button" onClick={() => removerNota(editingNote.id)} className="p-1 text-muted hover:text-red rounded">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                  <textarea
                    value={editingDecoded.body}
                    onChange={(e) => updateNota(editingNote.id, { body: e.target.value })}
                    placeholder="Escreve aqui…"
                    className="flex-1 input rounded-none border-0 text-xs leading-relaxed resize-none focus:outline-none px-3 py-2 min-h-[120px]"
                  />
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-muted text-[11px] p-3 text-center">
                  Selecione uma nota ou crie uma nova.
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ── Componentes auxiliares ── */

function Card({ icon: Icon, title, count, action, children }: {
  icon: typeof LayoutDashboard;
  title: string;
  count: number;
  action?: { label: string; onClick: () => void };
  children: React.ReactNode;
}) {
  return (
    <div className="bg-panel border border-border rounded-xl overflow-hidden">
      <div className="px-3 py-2.5 border-b border-border flex items-center justify-between bg-panel-2/40">
        <div className="flex items-center gap-2">
          <Icon className="w-3.5 h-3.5 text-blue-light" />
          <span className="text-xs font-bold">{title}</span>
          <span className="text-[10px] text-muted">({count})</span>
        </div>
        {action && (
          <button type="button" onClick={action.onClick}
            className="text-[10px] text-blue-light hover:text-blue flex items-center gap-0.5">
            {action.label} <ChevronRight className="w-3 h-3" />
          </button>
        )}
      </div>
      <div className="px-3 py-2">
        {children}
      </div>
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2 py-4 justify-center text-muted text-[11px]">
      <AlertCircle className="w-3.5 h-3.5 opacity-60" />
      {text}
    </div>
  );
}

